import { useContext } from 'react'

import { faEyeSlash } from '@fortawesome/pro-light-svg-icons/faEyeSlash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { OptimizelyContext } from '@optimizely/react-sdk'
import Button from '@space-core/components/button'
import type { Variation } from '@space-core/components/button'
import LineClamp from '@space-core/components/lineClamp'
import Tooltip from '@space-core/components/tooltip'
import useTranslation from '@space-core/hooks/useTranslation'
import { ProductPrice } from '@space-shell/common/components/productPrice'
import { AuthStatus, useAuthContext } from '@space-shell/common/contexts/auth'
import useThemeContext from '@space-shell/common/hooks/useThemeContext'
import { ShowcaseOrientation } from '@space-shell/common/services/membership'
import useProductClick from '@space-shell/modules/home/hooks/useProductClick'
import { verifyIfIsAPhysicalProduct } from '@space-shell/modules/products/utils/physicalProduct'
import { useSalesPage } from '@space-shell/modules/sales/presentation/hooks/common/use-sales-page'

import ButtonStudent from './buttonStudent'
import { LINE_AMOUNT_LG, LINE_AMOUNT_SM } from './constants'
import useGetProductPrice from './hooks/useGetProductPrice'
import useGetProductURL from './hooks/useGetProductUrl'
import { Container, CTAContainer, Footer, Info, ProductStatus, Wrapper } from './styles'
import TagsContainer from './tagsContainer'
import Thumbnail from './thumbnail'

import type {
  ProductCardProps,
  getCallToActionsProps,
  PhysicalProductProps,
  GeneralProductProps
} from './types'

import useProduct from '../../context/hook'
import { getPhysicalProductActions } from '../../utils/getPhysicalProductCallToActions'
import { getProductCallToActions } from '../../utils/getProductCallToActions'

const ProductCard = ({ product, orientation, sectionId, isPreview }: ProductCardProps) => {
  const { theme } = useThemeContext()
  const {
    t,
    i18n: { language }
  } = useTranslation()
  const handleProductClick = useProductClick({ product, sectionId })
  const internalSalesPageUrl = useSalesPage({ product })
  const { isTestDrawerProduct, methods } = useProduct()

  const {
    title,
    image,
    joined,
    bwImage,
    category,
    purchased,
    freeSignUp,
    avatarImage,
    description,
    verticalImage,
    horizontalImage,
    productHotLink,
    status
  } = product

  const { checkoutPrice, showPrice } = useGetProductPrice({ product, language, purchased })
  const isHorizontal = orientation === ShowcaseOrientation.HORIZONTAL
  const productUrl = useGetProductURL({
    product,
    internalSalesPageUrl
  })

  const { optimizely } = useContext(OptimizelyContext)
  const { authStatus } = useAuthContext()
  const isAuthenticated = authStatus === AuthStatus.AUTHENTICATED
  const isAPhysicalProduct = verifyIfIsAPhysicalProduct(String(category))

  const getActionsForPhysicalProduct = (params: PhysicalProductProps) =>
    getPhysicalProductActions(params)

  const getActionsForGeneralProduct = (params: GeneralProductProps) =>
    getProductCallToActions(params)

  const getCallToActions = (isAPhysicalProduct: boolean, params: getCallToActionsProps) =>
    isAPhysicalProduct
      ? getActionsForPhysicalProduct(params as PhysicalProductProps)
      : getActionsForGeneralProduct(params as GeneralProductProps)

  const callToActions = getCallToActions(isAPhysicalProduct, {
    joined,
    purchased,
    freeSignUp,
    productUrl,
    productHotLink,
    internalSalesPageUrl
  })

  const handleEventOptimizelyUserForSale = () => {
    if (freeSignUp && !isAuthenticated) {
      optimizely?.track('click_free_products')
      optimizely?.track('click_login_primary')
    } else if (!freeSignUp && !isAuthenticated) {
      optimizely?.track('click_for_sale_products')
    }
  }

  const handleProductClickValidate = (event: React.MouseEvent<HTMLElement>, productUrl: string) => {
    if (isTestDrawerProduct && !freeSignUp && !isAPhysicalProduct) {
      methods.setProduct(product)
    } else {
      handleProductClick(event, productUrl)
    }
    handleEventOptimizelyUserForSale()
  }

  return (
    <div>
      <Wrapper
        role='button'
        data-test='product-card'
        $orientation={orientation}
        className='_rounded-lg _cursor-pointer'
        {...(!isPreview && {
          onClick: (event) => {
            handleProductClickValidate(event, productUrl)
          }
        })}>
        <div className='_position-relative _d-block _h-full _w-full'>
          <Container className='_position-absolute _rounded-lg _d-flex _align-items-center _justify-content-center _bg-gray-200'>
            <TagsContainer product={product} />
            <Thumbnail
              image={image}
              title={title}
              bwImage={bwImage}
              avatar={avatarImage}
              orientation={orientation}
              verticalImage={verticalImage}
              horizontalImage={horizontalImage}
              version={product?.version}
            />

            <Info className='_position-absolute _p-5 _rounded-bottom _w-full' $theme={theme}>
              <LineClamp lines={2}>
                <h2 className='_text-3 _text-gray-900 _font-weight-bold'>{title}</h2>
              </LineClamp>

              <p className='_text-2 _text-gray-600 _m-0 _mb-2'>{t(`products.${category}`)}</p>

              <LineClamp
                lines={showPrice || isHorizontal ? LINE_AMOUNT_SM : LINE_AMOUNT_LG}
                className='_text-2 _text-gray-900 _mb-2'>
                <p className='_m-0'>{description}</p>
              </LineClamp>

              {showPrice && (
                <ProductPrice checkoutPrice={checkoutPrice} product={product} isSectionProduct />
              )}

              <CTAContainer className='_d-flex'>
                {callToActions.map((cta) => (
                  <Button
                    key={cta.label}
                    variation={cta.type as Variation}
                    className='_w-full _mt-5'
                    {...(!isPreview && {
                      onClick: (event) => {
                        handleProductClickValidate(event, cta.to)
                      }
                    })}>
                    {t(cta.label)}
                  </Button>
                ))}
                <ButtonStudent
                  isAPhysicalProduct={isAPhysicalProduct}
                  freeSignUp={freeSignUp}
                  isAuthenticated={isAuthenticated}
                  isPreview={isPreview}
                />
              </CTAContainer>
            </Info>
          </Container>
        </div>
        <Footer>
          <LineClamp lines={2}>
            <h2 className='_text-2 _text-gray-900 _font-weight-bold _mt-2 _mt-md-3 _mb-1'>
              {title}
            </h2>
          </LineClamp>
          {showPrice && (
            <ProductPrice checkoutPrice={checkoutPrice} product={product} isSectionProduct />
          )}
        </Footer>
      </Wrapper>

      {isPreview && status === 'DRAFT' && (
        <ProductStatus className='_d-flex _align-items-center _justify-content-center _position-absolute _text-gray-900 _bg-primary _text-center _px-3 _py-2'>
          <Tooltip content={t('preview.sections.sales.product_in_draft_tooltip')} position='top'>
            <div className='_cursor-pointer'>
              <FontAwesomeIcon icon={faEyeSlash} className='_mr-2' />
              <span>{t('preview.sections.sales.draft_tag')}</span>
            </div>
          </Tooltip>
        </ProductStatus>
      )}
    </div>
  )
}

export default ProductCard
